import fs from 'node:fs';
import http from 'node:http';
import https from 'node:https';
import { defined } from '@entwico/dash';
import { checks, server as healthServer, probes } from '@entwico/health-probes';
import { SpanStatusCode } from '@opentelemetry/api';
import { createApp } from 'astro/app/entrypoint';
import { setGetEnv } from 'astro/env/setup';
// @ts-expect-error virtual module provided by the integration
import { options } from 'virtual:@astroscope/node/config';
import { activateHealthChecks, deactivateHealthChecks } from '../health/store.js';
import { setBootContext } from '../lifecycle/context.js';
import { type BootModule, runShutdown, runStartup } from '../lifecycle/lifecycle.js';
import type { BootContext } from '../lifecycle/types.js';
import { createRequestInstrumentation } from '../observability/instrument.js';
import { dumpEarlyLogs } from '../observability/log/construct.js';
import { log } from '../observability/log/index.js';
import { type StartedSpan, startSpan, withSpan } from '../observability/telemetry/index.js';
import { shutdownTelemetry } from '../observability/telemetry/sdk.js';
import { preparePlatform } from '../platform/prepare.js';
import type { RuntimeOptions } from '../types.js';
import { enforceBodyLimit } from './body-limit.js';
import { resolveClientDir } from './client-dir.js';
import { clearNativeMounts, dispatchNativeMount } from './native-mount.js';
import { redirectPath } from './path-redirects.js';
import { createAppHandler } from './serve-app.js';
import { createStaticHandler } from './serve-static.js';

setGetEnv((key) => process.env[key]);

const runtimeOptions = options as RuntimeOptions;
const app = createApp({ streaming: true });

const roundMs = (n: number) => Math.round(n * 100) / 100;

/**
 * Pre-import every lazily loaded server module (pages, middleware, actions,
 * session driver) so the first request pays no import cost. Uses the
 * manifest's own loaders — exactly what the runtime calls per request.
 */
async function warmupModules(): Promise<void> {
  const loaders = [
    ...(app.manifest.pageMap?.values() ?? []),
    app.manifest.middleware,
    app.manifest.actions,
    app.manifest.sessionDriver,
    app.manifest.serverIslandMappings,
  ].filter(defined);

  const results = await Promise.allSettled(loaders.map((load) => Promise.resolve(load())));
  const failures = results.filter((result) => result.status === 'rejected');

  if (failures.length === 0) return;

  for (const failure of failures) {
    log.error(
      failure.reason instanceof Error ? { err: failure.reason } : { reason: failure.reason },
      'warmup import failed',
    );
  }

  // a module that cannot even be imported would throw on its first request;
  // failing the boot turns a silently-degraded deploy into a crash the health
  // checks catch before it takes traffic
  throw new AggregateError(
    failures.map((failure) => failure.reason),
    'warmup import failed',
  );
}

/**
 * TLS tokens from `SERVER_CERT_PATH` / `SERVER_KEY_PATH` (same contract as
 * `@astrojs/node`). Read after env loading, so the paths may come from `.env`.
 */
function loadTlsOptions(): { cert: Buffer; key: Buffer } | undefined {
  const certPath = process.env['SERVER_CERT_PATH'];
  const keyPath = process.env['SERVER_KEY_PATH'];

  if (!certPath && !keyPath) return undefined;

  if (!certPath || !keyPath) {
    throw new Error('SERVER_CERT_PATH and SERVER_KEY_PATH must both be set to serve HTTPS');
  }

  return { cert: fs.readFileSync(certPath), key: fs.readFileSync(keyPath) };
}

export interface ServerHandle {
  host: string;
  port: number;
  stop(): Promise<void>;
  closed(): Promise<void>;
}

export async function startServer(overrides?: {
  host?: string | undefined;
  port?: number | undefined;
}): Promise<ServerHandle> {
  const startedAt = performance.now();
  const host = overrides?.host ?? process.env['HOST'] ?? runtimeOptions.host;
  const port = overrides?.port ?? (process.env['PORT'] ? Number(process.env['PORT']) : runtimeOptions.port);
  const context: BootContext = { dev: false, host, port };
  const health = runtimeOptions.health;

  setBootContext(context);

  try {
    await preparePlatform({
      dev: false,
      telemetry: runtimeOptions.telemetry ? { prometheus: runtimeOptions.telemetry.prometheus } : false,
      seams: {
        config: () => import('virtual:@astroscope/node/config-entry'),
        instrumentation: () => import('virtual:@astroscope/node/instrumentation-entry'),
        log: () => import('virtual:@astroscope/node/log-entry'),
      },
    });
  } catch (error) {
    // the logger never came up — no silent phase, dump the buffer and die
    dumpEarlyLogs();
    console.error(error);
    process.exit(1);
  }

  if (health) {
    healthServer.start({ ...health, host: health.host ?? process.env['HEALTH_HOST'] ?? '0.0.0.0' });
    probes.live.enable();
    activateHealthChecks(checks);
    log.debug('health probes listening');
  }

  const startup = startSpan('startup');

  log.info({ host, port }, 'starting');

  // the boot module graph may read config at import time, so it must only be
  // evaluated after preparePlatform() has loaded env and config
  let bootModule: BootModule = {};
  let bootMs = 0;
  let warmupMs = 0;

  // starts in parallel with the boot startup, awaited before listen. resolves
  // to the failure (if any) instead of rejecting, so a warmup error landing
  // before the join below is never seen as an unhandled rejection
  const warmupStartedAt = performance.now();
  const warmupSpan = startSpan('warmup', { parent: startup.context });
  const settleWarmup = async (): Promise<unknown> => {
    try {
      await warmupModules();
    } catch (error) {
      warmupMs = roundMs(performance.now() - warmupStartedAt);
      warmupSpan.span.setStatus({ code: SpanStatusCode.ERROR, message: 'warmup import failed' });
      warmupSpan.span.end();

      return error;
    }

    warmupMs = roundMs(performance.now() - warmupStartedAt);
    warmupSpan.span.end();

    return undefined;
  };
  const warmup = settleWarmup();

  const shutdownLifecycle = async (shutdownContext?: StartedSpan['context']): Promise<void> => {
    try {
      if (shutdownContext) {
        await withSpan('onShutdown', { parent: shutdownContext }, () => runShutdown(bootModule, context));
      } else {
        await runShutdown(bootModule, context);
      }
    } catch (error) {
      log.error(error instanceof Error ? { err: error } : { reason: error }, 'shutdown failed');
    }

    clearNativeMounts();

    if (!health) {
      return;
    }

    deactivateHealthChecks();

    try {
      await healthServer.stop();
    } catch (error) {
      // a startup failure can tear the health server down before it has
      // finished binding; stopping it is best-effort
      log.debug(error instanceof Error ? { err: error } : { reason: error }, 'health probe server stop failed');
    }
  };

  const failStartup = async (err: unknown, message: string): Promise<never> => {
    log.error(err instanceof Error ? { err } : { reason: err }, message);
    startup.span.setStatus({ code: SpanStatusCode.ERROR, message });
    startup.span.end();

    await shutdownLifecycle();
    await shutdownTelemetry();
    process.exit(1);
  };

  try {
    const bootStartedAt = performance.now();

    // @ts-expect-error virtual module provided by the integration
    bootModule = (await import('virtual:@astroscope/node/boot')) as BootModule;

    await withSpan('boot', { parent: startup.context }, () => runStartup(bootModule, context));

    bootMs = roundMs(performance.now() - bootStartedAt);
  } catch (error) {
    await failStartup(error, 'startup failed');
  }

  const warmupError = await warmup;

  if (warmupError !== undefined) {
    await failStartup(warmupError, 'warmup import failed');
  }

  if (health) probes.startup.enable();

  const client = resolveClientDir(runtimeOptions, import.meta.url);
  const appHandler = createAppHandler(app, runtimeOptions, client);
  const staticHandler = createStaticHandler(app, client);
  const instrument = createRequestInstrumentation({
    logging: runtimeOptions.logging,
    telemetry: runtimeOptions.telemetry ? { exclude: runtimeOptions.telemetry.exclude } : false,
  });

  let tls: { cert: Buffer; key: Buffer } | undefined;

  try {
    tls = loadTlsOptions();
  } catch (error) {
    await failStartup(error, 'failed to load TLS options');
  }

  const listener: http.RequestListener = (req, res) => {
    try {
      decodeURI(req.url ?? '');
    } catch {
      res.writeHead(400);
      res.end('Bad request.');

      return;
    }

    instrument(req, res, () => {
      if (redirectPath(req, res, app.manifest.trailingSlash)) return;
      if (dispatchNativeMount(req, res)) return;
      if (enforceBodyLimit(req, res, runtimeOptions.bodySizeLimit)) return;

      staticHandler(req, res, () => void appHandler(req, res));
    });
  };

  const server = tls ? https.createServer(tls, listener) : http.createServer(listener);

  try {
    await withSpan('listen', { parent: startup.context }, () => {
      return new Promise<void>((resolve, reject) => {
        server.once('error', reject);
        server.listen(port, host, resolve);
      });
    });
  } catch (error) {
    await failStartup(error, `failed to listen on ${host}:${port}`);
  }

  if (health) probes.ready.enable();

  startup.span.setStatus({ code: SpanStatusCode.OK });
  startup.span.end();

  log.info(
    {
      host,
      port,
      ...(tls && { https: true }),
      health: !!health,
      bootMs,
      warmupMs,
      totalMs: roundMs(performance.now() - startedAt),
    },
    'server ready',
  );

  let stopPromise: Promise<void> | undefined;
  const { promise: closedPromise, resolve: resolveClosed } = Promise.withResolvers<void>();

  const doStop = async (): Promise<void> => {
    if (health) probes.ready.disable();

    log.info('shutdown initiated');

    const drainStartedAt = performance.now();
    const shutdown = startSpan('shutdown');

    await withSpan('drain', { parent: shutdown.context }, async () => {
      const closed = new Promise<void>((resolve) => server.close(() => resolve()));

      server.closeIdleConnections();

      const forceTimer = setTimeout(() => server.closeAllConnections(), runtimeOptions.shutdownTimeout);

      await closed;

      clearTimeout(forceTimer);
    });

    const drainMs = roundMs(performance.now() - drainStartedAt);

    await shutdownLifecycle(shutdown.context);

    shutdown.span.end();

    log.info({ drainMs }, 'shutdown complete');

    await shutdownTelemetry();
    resolveClosed();
  };

  const stop = (): Promise<void> => (stopPromise ??= doStop());

  process.once('SIGTERM', () => void stop().then(() => process.exit(0)));
  process.once('SIGINT', () => void stop().then(() => process.exit(0)));

  return { host, port, stop, closed: () => closedPromise };
}

if (process.env['ASTROSCOPE_NODE_AUTOSTART'] !== 'disabled') {
  await startServer();
}
